import { Circle } from 'lucide-react';
import styles from './IndicadorGrabacion.module.css';

export interface IndicadorGrabacionProps {
  /** Whether the recording is actively running */
  active: boolean;
  /** Display variant: 'badge' (small chip, default) | 'banner' (full-width strip) */
  variant?: 'badge' | 'banner';
  /** Optional accessible label override */
  label?: string;
}

/**
 * Always-visible "🔴 Grabando" indicator shown during a teleconsultation
 * to comply with HIPAA / consent-and-disclosure rules. Renders a pulsing
 * red dot and the word "Grabando".
 *
 * Should be placed in a high-contrast, always-visible position (e.g.
 * top-left corner of the video area) so neither the doctor nor the patient
 * can miss it.
 */
export function IndicadorGrabacion({
  active,
  variant = 'badge',
  label,
}: IndicadorGrabacionProps) {
  if (!active) return null;

  const text = label ?? 'Grabando';

  if (variant === 'banner') {
    return (
      <div
        className={styles.banner}
        role="status"
        aria-live="polite"
        aria-label={`${text}. Esta consulta se está grabando con fines clínicos.`}
      >
        <span className={styles.dot} aria-hidden="true" />
        <strong>{text}</strong>
        <span className={styles.bannerHint}>
          Esta consulta se está grabando con fines clínicos.
        </span>
      </div>
    );
  }

  return (
    <div
      className={styles.badge}
      role="status"
      aria-live="polite"
      aria-label={`${text}. Esta consulta se está grabando.`}
    >
      <span className={styles.dotWrap} aria-hidden="true">
        <Circle size={8} fill="currentColor" />
      </span>
      <span className={styles.label}>{text}</span>
    </div>
  );
}
